import React, { useState } from 'react';
import { api, apiErrorMessage, getToken } from '../api/client';

const ReportsPage: React.FC = () => {
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMsg(null);
    setPreview(null);
    if (!reason.trim()) {
      setError('A business reason is required for audit before exporting.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.get('/api/admin/reports/county-compliance', {
        params: { format, reason },
        responseType: format === 'csv' ? 'blob' : 'json',
      });

      if (format === 'csv') {
        const blob = new Blob([res.data as BlobPart], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'dh680-county-compliance.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        setMsg('CSV export downloaded.');
      } else {
        setPreview(JSON.stringify(res.data, null, 2));
        setMsg(`Generated report for ${res.data.total} records.`);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Export failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">
        County Analytics &amp; Compliance Export
      </h1>

      <div
        role="status"
        className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <strong>Florida DOH Reporting Safeguard:</strong> Verify every student record against the
        Florida Immunization Guidelines (DH 150-615) and Florida SHOTS before certifying or
        submitting DH 684 or FLDOE state reports. Unverified guardian submissions are held in
        Pending Review.
      </div>

      <form onSubmit={generate} className="space-y-4 rounded-xl bg-white p-5 shadow-sm">
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {msg && (
          <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
            {msg}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700">Report Type</label>
          <select
            disabled
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option>DH 680 County Compliance Summary</option>
          </select>
        </div>

        <div>
          <span className="block text-sm font-medium text-slate-700">File Format</span>
          <div className="mt-1 flex gap-4 text-sm">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={format === 'csv'}
                onChange={() => setFormat('csv')}
              />
              CSV (Spreadsheet)
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={format === 'json'}
                onChange={() => setFormat('json')}
              />
              JSON
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">
            Business Reason for Export (required for audit)
          </label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Florida Annual School Immunization Report submission"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        <button
          type="submit"
          disabled={busy || !getToken()}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Generating…' : 'Generate & Export Report'}
        </button>
      </form>

      {preview && (
        <pre className="max-h-96 overflow-auto rounded-xl bg-slate-900 p-4 text-xs text-slate-100">
          {preview}
        </pre>
      )}
    </div>
  );
};

export default ReportsPage;
